import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PreviewIcon from "@mui/icons-material/Preview";
import CheckIcon from "@mui/icons-material/Check";
import { toast } from "react-toastify";
import { Redirect } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";

toast.configure();

const money = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
});

const formatDate = (value) => {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-GB").format(new Date(value));
};

const readResponse = async (response) => {
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.type === "error") {
    throw new Error(body.message || "Server error. Please contact support.");
  }
  return body;
};

const SummaryValue = ({ label, value, emphasis = false }) => (
  <Box sx={{ minWidth: 150 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography fontWeight={emphasis ? 700 : 500} fontSize={emphasis ? 18 : 15}>
      {value}
    </Typography>
  </Box>
);

const BulkBillTransfer = () => {
  const userToken = LocalstorageHelper.GetUserToken();
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [parties, setParties] = useState([]);
  const [selection, setSelection] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedParty, setSelectedParty] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const requestHeaders = useMemo(
    () => ({ "Content-Type": "application/json", sptoken: userToken }),
    [userToken],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const responses = await Promise.all([
        fetch("/bulk-bill-transfer/bills", { headers: requestHeaders }),
        fetch("/bulk-bill-transfer/items", { headers: requestHeaders }),
        fetch("/bulk-bill-transfer/parties", { headers: requestHeaders }),
      ]);
      const [billData, itemData, partyData] = await Promise.all(
        responses.map(readResponse),
      );
      setRows(billData);
      setItems(itemData);
      setParties(partyData);
      setSelection([]);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [requestHeaders]);

  useEffect(() => {
    if (userToken) loadData();
  }, [loadData, userToken]);

  const selectedRows = useMemo(() => {
    const selectedIds = new Set(selection.map(Number));
    return rows.filter((row) => selectedIds.has(Number(row.billheaderid)));
  }, [rows, selection]);

  const selectedTotal = useMemo(
    () => selectedRows.reduce((sum, row) => sum + Number(row.totbillamt || 0), 0),
    [selectedRows],
  );

  const resetDialog = () => {
    setSelectedItem(null);
    setSelectedParty(null);
    setPreview(null);
    setConfirmOpen(false);
  };

  const closeDialog = () => {
    if (submitting) return;
    setDialogOpen(false);
    resetDialog();
  };

  const getPayload = () => ({
    billIds: selection.map(Number),
    itemId: Number(selectedItem?.itemid),
    partyId: Number(selectedParty?.subledgerid),
  });

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const response = await fetch("/bulk-bill-transfer/preview", {
        method: "POST",
        headers: requestHeaders,
        body: JSON.stringify(getPayload()),
      });
      const data = await readResponse(response);
      setPreview(data.preview);
    } catch (error) {
      setPreview(null);
      toast.error(error.message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleExecute = async () => {
    setSubmitting(true);
    try {
      const response = await fetch("/bulk-bill-transfer/execute", {
        method: "POST",
        headers: requestHeaders,
        body: JSON.stringify(getPayload()),
      });
      const data = await readResponse(response);
      toast.success(data.message, { autoClose: false });
      setConfirmOpen(false);
      setDialogOpen(false);
      resetDialog();
      await loadData();
    } catch (error) {
      setConfirmOpen(false);
      setPreview(null);
      toast.error(error.message, { autoClose: false });
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { field: "billno", headerName: "Bill No.", minWidth: 155, flex: 0.8 },
    {
      field: "billdate",
      headerName: "Bill Date",
      width: 110,
      valueFormatter: ({ value }) => formatDate(value),
    },
    { field: "subledgercode", headerName: "Party Code", width: 105 },
    { field: "subledgername", headerName: "Party Name", minWidth: 230, flex: 1.3 },
    {
      field: "saletype",
      headerName: "Type",
      width: 85,
      valueFormatter: ({ value }) => (Number(value) === 1 ? "Invoice" : "Spare"),
    },
    {
      field: "totgross",
      headerName: "Gross",
      width: 115,
      type: "number",
      valueFormatter: ({ value }) => money.format(Number(value || 0)),
    },
    {
      field: "totdiscount",
      headerName: "Discount",
      width: 115,
      type: "number",
      valueFormatter: ({ value }) => money.format(Number(value || 0)),
    },
    {
      field: "totbillamt",
      headerName: "Bill Amount",
      width: 125,
      type: "number",
      valueFormatter: ({ value }) => money.format(Number(value || 0)),
    },
    { field: "eligibilityreason", headerName: "Reason", minWidth: 190, flex: 1 },
  ];

  if (!userToken) return <Redirect exact to="/sparepartsLogin" />;

  return (
    <Sidebar
      Title="BULK BILL TRANSFER"
      expandedPanel="panel2"
      children={
        <Box sx={{ width: "100%", px: { xs: 1, md: 3 }, pb: 4 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            spacing={2}
            mb={2}
          >
            <Box>
              <Typography variant="h6">Eligible unregistered bills</Typography>
              <Typography variant="body2" color="text.secondary">
                {selection.length} selected | {money.format(selectedTotal)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                startIcon={<RefreshIcon />}
                onClick={loadData}
                disabled={loading}
                variant="outlined"
              >
                Refresh
              </Button>
              <Button
                startIcon={<SwapHorizIcon />}
                onClick={() => setDialogOpen(true)}
                disabled={!selection.length || loading}
                variant="contained"
              >
                Convert Selected
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ height: "calc(100vh - 245px)", minHeight: 430, border: "1px solid", borderColor: "divider" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.billheaderid}
              checkboxSelection
              disableSelectionOnClick
              selectionModel={selection}
              onSelectionModelChange={(model) => setSelection(model)}
              loading={loading}
              density="compact"
              pageSize={25}
              rowsPerPageOptions={[25, 50, 100]}
              sx={{ border: 0 }}
            />
          </Box>

          <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
            <DialogTitle>Prepare bulk transfer</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <SummaryValue label="Selected bills" value={selection.length} />
                  <SummaryValue label="Selected total" value={money.format(selectedTotal)} emphasis />
                </Stack>
                <Divider />
                <Autocomplete
                  options={items}
                  value={selectedItem}
                  onChange={(_, value) => {
                    setSelectedItem(value);
                    setSelectedParty(null);
                    setPreview(null);
                  }}
                  getOptionLabel={(option) =>
                    `${option.itemcode} - ${option.itemname} (Stock ${option.closingqty})`
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Rs. 1 MRP item" required />
                  )}
                />
                {selectedItem && (
                  <Autocomplete
                    options={parties}
                    value={selectedParty}
                    onChange={(_, value) => {
                      setSelectedParty(value);
                      setPreview(null);
                    }}
                    getOptionLabel={(option) =>
                      `${option.subledgercode} - ${option.subledgername} - ${option.gstno}`
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Registered party" required />
                    )}
                  />
                )}
                {preview && (
                  <>
                    <Divider />
                    <Stack direction="row" useFlexGap flexWrap="wrap" gap={2.5}>
                      <SummaryValue label="Average discount" value={`${Number(preview.averageDiscountRate).toFixed(4)}%`} />
                      <SummaryValue label="Rs. 1 qty required" value={preview.requiredTransferItemQty} />
                      <SummaryValue label="Rs. 1 qty available" value={preview.availableTransferItemQty} />
                      <SummaryValue label="Taxable" value={money.format(preview.taxable)} />
                      <SummaryValue label="GST" value={money.format(Number(preview.cgst) + Number(preview.sgst) + Number(preview.igst))} />
                      <SummaryValue label="New invoice" value={money.format(preview.billAmount)} emphasis />
                      <SummaryValue label="Variance" value={money.format(preview.variance)} emphasis />
                    </Stack>
                    {Math.abs(Number(preview.variance)) > 1 && (
                      <Alert severity="warning">
                        The recalculated invoice differs from the selected total by {money.format(preview.variance)}.
                      </Alert>
                    )}
                  </>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} disabled={submitting}>Cancel</Button>
              <Button
                startIcon={previewing ? <CircularProgress size={16} /> : <PreviewIcon />}
                onClick={handlePreview}
                disabled={!selectedItem || !selectedParty || previewing || submitting}
                variant="outlined"
              >
                Preview
              </Button>
              <Button
                startIcon={<CheckIcon />}
                onClick={() => setConfirmOpen(true)}
                disabled={!preview || previewing || submitting}
                variant="contained"
                color="success"
              >
                Create Invoice
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)}>
            <DialogTitle>Confirm bulk bill transfer</DialogTitle>
            <DialogContent dividers>
              <Alert severity="warning">
                The selected source bills will be replaced with the chosen Rs. 1 item and one registered invoice will be created for {money.format(preview?.billAmount || 0)}.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>Back</Button>
              <Button
                onClick={handleExecute}
                disabled={submitting}
                variant="contained"
                color="success"
                startIcon={submitting ? <CircularProgress size={16} /> : <CheckIcon />}
              >
                Confirm Transfer
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      }
    />
  );
};

export default BulkBillTransfer;
